import { computed, ref, watch } from 'vue'

import { defineStore } from 'pinia'

import { useDocument, useFirestore } from 'vuefire'

import { collection, doc, getDocFromServer, setDoc, updateDoc } from 'firebase/firestore'

import useOuraAuth from '#web-oura-tools/auth'

import type { Service } from '#ourahub-intra/types/services'
import type { Account, Preferences } from '#ourahub-intra/types/users'

export const useAppStore = defineStore('AppStore', () => {
  const ouraAuth = useOuraAuth()
  const fbFirestore = useFirestore()

  const searchOrTask = ref('')
  const activeService = ref('')

  const showOnlyMyApps = ref(false)
  const showUnderDevel = ref(false)

  const searchActions = ref<Service[]>([])
  const searchResults = ref<Service[]>([])

  const presetCategories = ref<string[]>([])
  const selectedCategories = ref<string[]>([])

  const serviceVersions = ref<Record<string, string>>()
  const serviceCategories = ref<Record<string, string>>({})

  // Only update service versions once on service load so that storing them wont overwrite state!
  // Note that we can not use the vuefire loaded data below because it might return local cached data!
  watch(
    () => ouraAuth.user.value,
    async (user) => {
      if (user) {
        const snapshot = await getDocFromServer(doc(collection(fbFirestore, 'account-data'), user.id))

        serviceVersions.value = snapshot.data()?.stats?.notifiedServiceVersions || {}
      }
    },
    { immediate: true },
  )

  const { data: accountData, pending: accountDataLoading } = useDocument<Account>(() =>
    ouraAuth.user.value ? doc(collection(fbFirestore, 'account-data'), ouraAuth.user.value.id) : null,
  )

  const { data: accountPrefs, pending: accountPrefsLoading } = useDocument<Preferences>(() =>
    ouraAuth.user.value ? doc(collection(fbFirestore, 'account-prefs'), ouraAuth.user.value.id) : null,
  )

  async function updateAccountPrefs(prefs: Partial<Preferences>) {
    if (ouraAuth.user.value) {
      await setDoc(doc(collection(fbFirestore, 'account-prefs'), ouraAuth.user.value.id), prefs, { merge: true })
    }
  }

  async function updateServiceVersions(versions: Record<string, string>) {
    if (ouraAuth.user.value?.id) {
      await updateDoc(doc(fbFirestore, 'account-data', ouraAuth.user.value.id), {
        'stats.notifiedServiceVersions': { ...serviceVersions.value, ...versions },
      })
    }
  }

  return {
    isLoading: computed(() => accountDataLoading.value || accountPrefsLoading.value),

    accountData,
    accountPrefs,

    activeService,

    searchOrTask,
    searchActions,
    searchResults,

    showOnlyMyApps,
    showUnderDevel,

    serviceVersions,
    presetCategories,
    serviceCategories,
    selectedCategories,

    updateAccountPrefs,
    updateServiceVersions,
  }
})
